/* eslint-disable react/prop-types */
import Balloon from './Balloon'
import Icon from './Icon'
import { tick } from '../lib/sound'

// Projects as album panels in a 12-column tier grid. Hover or focus holds one panel
// in full colour with a heavier line while the rest of the tier rests paler, and the
// panel speaks its description (shown under the thumbnail on touch screens). State changes are clean cuts.
// `spans` sets each panel's width by index; the last entry repeats.
const ProjectList = ({ items, spans = ['md:col-span-6 lg:col-span-3'], children }) => (
  <ul className='group/tier grid grid-cols-1 gap-tier md:grid-cols-12'>
    {items.map((p, i) => (
      <li
        key={p.title}
        onPointerEnter={() => tick(520 + i * 30)}
        className={`panel group/p flex flex-col outline-[length:var(--stroke)] outline-signal group-hover/tier:opacity-55 hover:!opacity-100 hover:outline focus-within:!opacity-100 focus-within:outline ${spans[Math.min(i, spans.length - 1)]}`}
      >
        <div className='flex items-start'>
          <h3 className='caption'>
            {/* The ::after stretches the link over the whole panel, thumbnail included. */}
            <a
              href={p.link}
              target='_blank'
              rel='noreferrer'
              className='outline-none after:absolute after:inset-0 after:z-[5]'
            >
              {p.title}
              <span className='sr-only'> ({p.linkLabel ?? 'live site'}, opens in a new tab)</span>
            </a>
          </h3>
          {p.client && (
            <p className='lettering border-b-stroke border-r-stroke border-line bg-sky px-3 py-1.5 text-coal'>
              Client work<span className='sr-only'> for {p.client}</span>
            </p>
          )}
        </div>

        <div className='relative m-3 overflow-hidden border-stroke border-line md:m-4'>
          <img
            src={p.image.src}
            srcSet={p.image.srcSet}
            sizes='(min-width: 1024px) 40vw, (min-width: 768px) 50vw, 100vw'
            alt={`Screenshot of ${p.title}`}
            loading='lazy'
            width='1200'
            height='800'
            className='block aspect-[3/2] w-full object-cover object-top'
          />
          <Balloon
            aria-hidden='true'
            className='pointer-events-none absolute left-2 right-8 top-2 z-10 hidden !rounded-[1.25rem] !px-3.5 !py-2 group-focus-within/p:block group-hover/p:block'
          >
            <p className='line-clamp-3 font-letter text-[0.9rem] leading-snug'>{p.description}</p>
          </Balloon>
        </div>

        <p className='px-3 text-body [@media(hover:hover)]:hidden md:px-4'>
          {p.description}
        </p>

        <div className='mt-auto flex items-start justify-between gap-4 px-3 pb-3 pt-1 md:px-4 md:pb-4'>
          <p className='lettering text-ink/75'>{p.stack}</p>
          {p.github && (
            <a
              href={p.github}
              target='_blank'
              rel='noreferrer'
              className='lettering relative z-20 inline-flex shrink-0 items-center gap-1 underline-offset-4 hover:underline'
            >
              Code <Icon name='out' className='size-4' />
              <span className='sr-only'> for {p.title} (opens in a new tab)</span>
            </a>
          )}
        </div>
      </li>
    ))}
    {children}
  </ul>
)

export default ProjectList
